import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Stack,
    Divider,
    Box,
    Chip,
    Button,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import useCurrency from '../../../hooks/common/useCurrency';
import { useUpdateOrder } from '../../../hooks/orders/useUpdateOrder';

const Row = ({ label, value, bold, color }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant={bold ? 'subtitle1' : 'body2'} fontWeight={bold ? 'bold' : 'normal'} color={color || 'text.primary'}>
            {label}
        </Typography>
        <Typography variant={bold ? 'subtitle1' : 'body2'} fontWeight={bold ? 'bold' : 'medium'} color={color || 'text.primary'}>
            {value}
        </Typography>
    </Stack>
);

const EDITABLE_STATUSES = new Set(['pending', 'confirmed']);

const OrderTotalsCard = ({ order }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const editable = EDITABLE_STATUSES.has(order?.status);

    const [couponInput, setCouponInput] = useState('');
    const { updateOrderMutate, isUpdating } = useUpdateOrder();

    const applyCoupon = () => {
        if (!couponInput) return;
        updateOrderMutate(
            { id: order.id, data: { coupon_code: couponInput } },
            { onSuccess: () => setCouponInput('') }
        );
    };

    const removeCoupon = () => {
        updateOrderMutate({ id: order.id, data: { coupon_code: null } });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'الإجماليات' : 'Totals'}
            </Typography>

            <Stack spacing={1.5}>
                <Row
                    label={isArabic ? 'المجموع الفرعي' : 'Subtotal'}
                    value={formatPrice(order?.subtotal ?? 0)}
                />

                <Box>
                    <Row
                        label={isArabic ? 'الخصم' : 'Discount'}
                        value={`- ${formatPrice(order?.discount_total ?? 0)}`}
                        color="error.main"
                    />
                    {order?.coupon?.code && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <Chip
                                size="small"
                                label={order.coupon.code}
                                color="primary"
                                variant="tonal"
                                sx={{ fontWeight: 'bold' }}
                            />
                            {editable && (
                                <Button size="small" color="error" onClick={removeCoupon} disabled={isUpdating}>
                                    {isArabic ? 'إزالة' : 'Remove'}
                                </Button>
                            )}
                        </Stack>
                    )}
                </Box>

                <Row
                    label={isArabic ? 'الشحن' : 'Shipping'}
                    value={formatPrice(order?.shipping_total ?? 0)}
                />

                <Row
                    label={isArabic ? 'الضريبة' : 'Tax'}
                    value={formatPrice(order?.tax_total ?? 0)}
                />

                <Divider />

                <Row
                    label={isArabic ? 'الإجمالي' : 'Grand Total'}
                    value={formatPrice(order?.grand_total ?? 0)}
                    bold
                />

                {(order?.refunded_amount ?? 0) > 0 && (
                    <Row
                        label={isArabic ? 'المبلغ المسترد' : 'Refunded'}
                        value={`- ${formatPrice(order.refunded_amount)}`}
                        color="warning.main"
                    />
                )}

                {editable && !order?.coupon?.code && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <TextField
                            size="small"
                            placeholder={isArabic ? 'كود كوبون' : 'Coupon code'}
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                            sx={{ flex: 1 }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={applyCoupon}
                            disabled={isUpdating || !couponInput}
                        >
                            {isArabic ? 'تطبيق' : 'Apply'}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};

export default OrderTotalsCard;
